import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { ProductCard } from '../components/ProductCard';
import { Filter, Loader } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Products() {
  const { t, isArabic } = useLanguage();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [productsRes, categoriesRes] = await Promise.all([
          fetch(`${API}/products?limit=100`),
          fetch(`${API}/categories`)
        ]);
        
        const productsData = await productsRes.json();
        const categoriesData = await categoriesRes.json();
        
        // Handle both old format (array) and new format (object with products)
        const productList = Array.isArray(productsData) ? productsData : (productsData.products || []);
        setProducts(productList);
        setCategories(categoriesData);
      } catch (err) {
        console.error('Error fetching data:', err);
        setProducts([]);
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(p => p.category === selectedCategory);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="products-loading">
        <Loader className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="products-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="products-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-100 to-white" />
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-black text-white text-xs font-medium rounded-full mb-6 tracking-wider uppercase">
              {isArabic ? 'منتجاتنا' : 'Our Products'}
            </span>
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {t('productsTitle')}
            </h1>
            <p className="text-lg md:text-xl text-slate-600">
              {t('productsSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="products-grid-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {/* Category Filter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center gap-3 mb-6">
              <Filter className="w-5 h-5 text-slate-500" />
              <span className="text-sm font-medium text-slate-600">
                {isArabic ? 'تصفية حسب الفئة' : 'Filter by Category'}
              </span>
            </div>
            
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedCategory === 'all'
                    ? 'bg-black text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
                data-testid="filter-all"
              >
                {isArabic ? 'الكل' : 'All'}
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === cat.id
                      ? 'bg-black text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${cat.id}`}
                >
                  {isArabic ? cat.nameAr : cat.nameEn}
                </button>
              ))}
            </div>
          </motion.div>

          {/* Products Count */}
          <div className="mb-8">
            <p className="text-sm text-slate-500">
              {isArabic 
                ? `عرض ${filteredProducts.length} منتج`
                : `Showing ${filteredProducts.length} products`
              }
            </p>
          </div>

          {/* Products Grid */}
          {filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-slate-500">
                {isArabic ? 'لا توجد منتجات في هذه الفئة' : 'No products in this category'}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="products-cta">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-black rounded-3xl p-8 md:p-12 text-center text-white"
          >
            <h2 className={`text-2xl md:text-3xl font-bold mb-4 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {isArabic ? 'لم تجد ما تبحث عنه؟' : "Can't find what you're looking for?"}
            </h2>
            <p className="text-slate-400 mb-6 max-w-lg mx-auto">
              {isArabic 
                ? 'تواصل معنا وسنساعدك في إيجاد المنتج المناسب لاحتياجاتك'
                : 'Contact us and we will help you find the right product for your needs'
              }
            </p>
            <a
              href="tel:0943999339"
              className="inline-flex items-center gap-2 bg-white text-black px-8 py-3 rounded-full font-medium hover:bg-slate-100 transition-colors duration-300"
              data-testid="products-call-btn"
            >
              <span dir="ltr">0943 999 339</span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
